import {enUS as enLocale} from "date-fns/locale/en-US";
import {nb as nbLocale} from "date-fns/locale/nb";
import {nn as nnLocale} from "date-fns/locale/nn";
import {sv as svLocale} from "date-fns/locale/sv";

import {add, format as dateFnsFormat, isPast} from "date-fns";
import {getBrowserLocale} from "../utility";
import {clientLog} from "../clientLog";

export const getLocale = () => {
    const selectedLanguage = getBrowserLocale(true);

    if ("sv" === selectedLanguage) {
        return svLocale;
    } else if ("no" === selectedLanguage || "nb" === selectedLanguage) {
        return nbLocale;
    } else if ("nn" === selectedLanguage) {
        return nnLocale;
    } else {
        return enLocale;
    }
};

export const format = (date, formatStr) => {
    if (Array.isArray(date)) {
        return '';
    }
    if (!date) {
        return date;
    }
    try {
        if (date instanceof Date) {
            return dateFnsFormat(date, formatStr, {locale: getLocale()});
        } else {
            const yearOnlyMask = /^(\d{4}-01-01)T00:00:00.000Z$/gi;
            const m = date.match(yearOnlyMask);
            if (m) {
                // Year only
                return date.match(/^(\d{4})/gi)[0];
            } else {
                return dateFnsFormat(new Date(date), formatStr, {locale: getLocale()});
            }
        }
    } catch (e) {
        const logMessage = `failed: ${date} - ${formatStr} - ${e.message}`;
        clientLog('warning', logMessage, 'dateformat');
        return '__ . __ . ____';
    }
};

export const getDateInOneHour = () => {
    return add(new Date(), {minutes: 59});
};

export const getDateInSevenDays = () => {
    return add(new Date(), {days: 7});
};

export const isDateInPast = (date) => isPast(date);

const OSLO_TIME_ZONE = "Europe/Oslo";

/**
 * Formats a date/timestamp for display in Norwegian (Europe/Oslo) local time, regardless of
 * the browser's own timezone. Handles the CET/CEST offset (including DST transitions)
 * automatically via Intl.DateTimeFormat, so callers never need to add/subtract the offset
 * themselves.
 *
 * Values coming from the backend are UTC; if they lack an explicit timezone designator
 * (e.g. a naive ISO string with no trailing 'Z' or '+00:00'), it is assumed to represent UTC
 * and one is appended before parsing - otherwise the JS Date constructor would (incorrectly)
 * interpret it as already being in the browser's local time.
 *
 * @param {Date|string|number|null} date
 * @returns {string} e.g. "23.09.2026, 15:15:30" (format depends on locale), or '' if date is
 *          falsy, or a placeholder if the date could not be parsed.
 */
export const formatInOsloTime = (date) => {
    if (!date) {
        return '';
    }
    try {
        let d = date;
        if (!(d instanceof Date)) {
            const hasTimezoneDesignator = typeof date === "string" && /(Z|[+-]\d{2}:?\d{2})$/i.test(date.trim());
            d = new Date(typeof date === "string" && !hasTimezoneDesignator ? `${date}Z` : date);
        }
        if (isNaN(d)) {
            throw new Error('invalid date');
        }

        let locale = getBrowserLocale();
        if (locale === 'no') {
            locale = 'nb-NO';
        }

        return new Intl.DateTimeFormat(locale, {
            timeZone: OSLO_TIME_ZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).format(d);
    } catch (e) {
        const logMessage = `failed: ${date} - ${e.message}`;
        clientLog('warning', logMessage, 'dateformat');
        return '__ . __ . ____';
    }
};
